/**
 * Guest Check-in Preview Parameter Composer
 *
 * Utility for building consistent preview parameters for Guest Check-in widget iframe.
 * Handles both template previews (template data overridden with user selections) and form editor previews (with real data).
 */

import {
  buildWidgetIframeSrc as buildBaseWidgetIframeSrc,
  sendSettingsToWidget,
  sendPageChangeToWidget,
  sendAutoFillTestData,
  createBaseWidgetSettings,
} from '@/js/widget/widget-preview.js';

/**
 * Compose preview parameters for the Guest Check-in widget
 * @param {Object} options - Configuration options
 * @param {Object} options.form - The form object (for editor previews)
 * @param {Object} options.template - The template object (for template card previews)
 * @param {string} options.organization_name - Organization name
 * @param {string} options.background_image_url - Background image URL
 * @param {string} options.side_image_url - Side image URL
 * @param {string} options.logo_url - Logo URL
 * @param {boolean} options.use_logo - Whether to show logo
 * @param {Object} options.user_consent_settings - User consent settings
 * @param {Object} options.comp_custom_fonts - Custom fonts data
 * @param {boolean} options.is_test_submission - Whether this is a test submission
 * @param {string} options.preview_mode - Preview mode ('tablet-landscape', 'tablet', 'mobile')
 * @param {boolean} options.is_template_preview - Whether this is a template preview (vs form editor)
 * @returns {Object} Composed parameters for the widget
 */
export function composeGuestCheckInPreviewParams(options = {}) {
  const {
    form,
    template,
    comp_custom_fonts = null,
    is_test_submission = true,
    preview_mode = 'tablet-landscape',
    is_template_preview = false,
  } = options;

  let form_data;
  if (is_template_preview && template) {
    // Backend handles all interpolation (colors, fonts, and images) via /template-types handler
    form_data = template.template_overrides || template;
  } else if (form) {
    // form editor preview: use actual settings data
    form_data = form;
  } else {
    throw new Error('Either settings or template must be provided');
  }

  const widget_settings = createBaseWidgetSettings({
    form_id_key: 'guest_check_in_form_id',
    form_id: form?.guest_check_in_form_id,
    form: form_data,
    is_test_submission,
    is_template_preview,
    preview_mode,
    page: 'form',
  });

  if (comp_custom_fonts) {
    widget_settings.comp_custom_fonts = comp_custom_fonts;
  }

  // for editor previews only
  if (widget_settings.form && form_data?.organization_name) {
    widget_settings.form.organization_name = form_data.organization_name;
  }

    // eslint-disable-next-line no-console
  console.log(
    '[OC Preview Composer] Final widget_settings created (keys):',
    Object.keys(widget_settings)
  );

  return widget_settings;
}

/**
 * Build guest check-in widget iframe source URL
 * @param {string} preview_mode - Preview mode ('tablet-landscape', 'tablet', 'mobile')
 * @param {boolean} is_development - Whether in development mode
 * @returns {string} Widget iframe source URL
 */
export function buildWidgetIframeSrc(preview_mode = 'tablet-landscape', is_development = false) {
  return buildBaseWidgetIframeSrc({
    widget_name: 'guest-check-in',
    preview_mode,
    is_development,
    dev_port: 5175,
  });
}

export { sendSettingsToWidget, sendPageChangeToWidget, sendAutoFillTestData };
